import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { normalizeCollection } from "@/lib/api/platform";
import { API_ENDPOINTS } from "../config/constants";

/**
 * Query key factory for home dashboard
 */
export const homeDashboardQueryKeys = {
  all: ["home-dashboard"] as const,
  summary: () => [...homeDashboardQueryKeys.all, "summary"] as const,
};

/**
 * Hook to fetch dashboard summary data from /api/dashboard/summary
 *
 * Provides:
 * - Aggregated dashboard data for the home page
 * - Real-time updates via Mercure
 * - Automatic caching with 5-minute stale time
 * - Auto-refresh every 2 minutes
 */
export function useDashboardSummary() {
  // Mercure subscription lives in lib/config/mercureSubscriptions.ts
  // (topic: /api/dashboard/summary) so invalidation works app-wide.

  return useQuery({
    queryKey: homeDashboardQueryKeys.summary(),
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.DASHBOARD_SUMMARY);
      const data = response.data;
      if (!data || typeof data !== "object") return data;

      // API Platform wraps array-typed DTO properties in a JSON-LD Collection
      // envelope ({ totalItems, member: [...] }) — unwrap them so consumers
      // always see plain arrays (same pattern as getCampagnesCp).
      return {
        ...data,
        recentAbsences: normalizeCollection(data.recentAbsences),
        recentTimesheets: normalizeCollection(data.recentTimesheets),
        pendingApprovals: normalizeCollection(data.pendingApprovals),
        teamAbsencesToday: normalizeCollection(data.teamAbsencesToday),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 2 * 60 * 1000, // Refresh every 2 minutes
  });
}
